import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { problemTitle } from './usePartsLibrary'

/**
 * UID-170 (Story F2) / UID-169 (F1) — "Add to project" row + bulk-bar action
 * (screens.md §5). Types are reused VERBATIM from the generated contract
 * (`schema.gen.ts`), never hand-typed, per the F0/F1 handoff:
 *   - `AddPartToWorkspaceRequest` is a `oneOf` — EXACTLY ONE of an existing
 *     `workspaceId` OR an inline `newWorkspace` (the "New project" branch,
 *     reusing `CreateWorkspaceRequest` verbatim).
 *   - `PartWorkspaceLink` is the resulting association, echoing the linked
 *     `WorkspaceSummary` so the FE can render a chip with no second fetch.
 */
export type AddPartToWorkspaceRequest = components['schemas']['AddPartToWorkspaceRequest']
export type PartWorkspaceLink = components['schemas']['PartWorkspaceLink']

export type AddToProjectOutcome =
  | { readonly ok: true; readonly link: PartWorkspaceLink }
  | { readonly ok: false; readonly message: string }

export interface AddToProjectService {
  addPartToWorkspace(
    partId: string,
    request: AddPartToWorkspaceRequest,
  ): Promise<AddToProjectOutcome>
}

/**
 * REAL BFF-backed call — `POST /parts/{partId}/workspaces` (F1, UID-169).
 * The link write is idempotent-friendly server-side (linking an
 * already-linked (part, workspace) pair returns the existing association,
 * not a duplicate), so this never needs its own client-side dedupe.
 */
async function bffAddPartToWorkspace(
  partId: string,
  request: AddPartToWorkspaceRequest,
): Promise<AddToProjectOutcome> {
  const { data, error } = await apiClient.POST('/parts/{partId}/workspaces', {
    params: { path: { partId } },
    body: request,
  })
  if (error || !data) {
    return {
      ok: false,
      message: problemTitle(error, "We couldn't add this part to that project. Try again."),
    }
  }
  return { ok: true, link: data }
}

export const defaultAddToProjectService: AddToProjectService = {
  addPartToWorkspace: bffAddPartToWorkspace,
}
